/**
 * Stack de mini-drawers de chat empilés à droite (style FB Messenger).
 *
 * - Rend jusqu'à MAX_OPEN_DRAWERS drawers côte à côte (desktop)
 * - Les convs "minimisées" ou au-delà du max apparaissent en bulles à gauche
 * - Un seul drawer est "focused" à la fois — reçoit les raccourcis clavier
 * - Sur mobile (< 640px) : un seul drawer visible plein écran + bulles au-dessus
 *
 * Intégré une seule fois dans MainLayout — écoute le slice openChats Redux.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  closeChat, focusChat, toggleMinimize, MAX_OPEN_DRAWERS,
  OpenChat,
} from '../../store/slices/openChatsSlice';
import { PortailChatDrawer } from './PortailChatDrawer';
import { useChatShortcuts } from '../../hooks/useChatShortcuts';
import { MessageCircle, X } from 'lucide-react';

function initiales(nom: string): string {
  return nom.split(' ').slice(0, 2).map(s => (s[0] || '').toUpperCase()).join('');
}

export const ChatDrawersStack: React.FC = () => {
  useChatShortcuts();

  const dispatch = useDispatch();
  const chats = useSelector((s: RootState) => s.openChats.chats);
  const focusedConvId = useSelector((s: RootState) => s.openChats.focusedConvId);
  const isDark = useSelector((s: RootState) => s.ui.theme) === 'dark';

  if (chats.length === 0) return null;

  // Drawers ouverts (non minimisés) — max 3 côté droit
  const openDrawers = chats.filter(c => !c.minimized).slice(0, MAX_OPEN_DRAWERS);
  // Bulles à gauche : conversations minimisées OU débordement au-delà de MAX
  const minimizedIds = new Set(chats.filter(c => c.minimized).map(c => c.convId));
  const overflow = chats.filter(c => !c.minimized).slice(MAX_OPEN_DRAWERS);
  const bubbleChats = [
    ...chats.filter(c => minimizedIds.has(c.convId)),
    ...overflow,
  ];

  return (
    <>
      {openDrawers.map((chat, idx) => (
        <PortailChatDrawer
          key={chat.convId}
          convId={chat.convId}
          partenaireLabel={chat.label}
          stackIndex={idx}
          isFocused={chat.convId === focusedConvId}
          isDark={isDark}
          onClose={() => dispatch(closeChat(chat.convId))}
          onMinimize={() => dispatch(toggleMinimize(chat.convId))}
          onFocus={() => dispatch(focusChat(chat.convId))}
        />
      ))}

      {bubbleChats.length > 0 && (
        <ChatBubblesColumn
          bubbles={bubbleChats}
          isDark={isDark}
          onOpen={(id) => dispatch(focusChat(id))}
          onClose={(id) => dispatch(closeChat(id))}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Bulles minimisées (colonne à droite, entre le bouton flottant et les drawers)
// ---------------------------------------------------------------------------

const ChatBubblesColumn: React.FC<{
  bubbles: OpenChat[];
  isDark: boolean;
  onOpen: (id: number) => void;
  onClose: (id: number) => void;
}> = ({ bubbles, isDark, onOpen, onClose }) => {
  // Position : au-dessus du bouton flottant messagerie (bottom-24)
  // Sur mobile : en haut de l'écran (les drawers plein écran)
  return (
    <div className="fixed z-[65] bottom-24 right-4 sm:bottom-6 sm:right-[1230px] flex flex-col-reverse gap-2 pointer-events-none">
      {bubbles.slice(0, 5).map((b) => (
        <div key={b.convId} className="pointer-events-auto relative group">
          <button
            onClick={() => onOpen(b.convId)}
            title={b.label}
            className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-2 font-bold text-sm transition-transform hover:scale-110 ${
              isDark
                ? 'bg-blue-700 border-blue-400 text-white'
                : 'bg-white border-blue-500 text-blue-700'
            }`}
          >
            {initiales(b.label) || <MessageCircle size={18} />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(b.convId); }}
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            title="Fermer"
          >
            <X size={11} />
          </button>
        </div>
      ))}
    </div>
  );
};
